/**
 * Submit a signed x402 payment back to the 402 Lambda.
 *
 * The 402 endpoint is on the API host (link.urls.full is the UUID
 * form). We rewrite link.urls.short/slug from WEBAPP_BASE_URL →
 * API_BASE_URL because the webapp-hosted short/slug URLs resolve to
 * the webapp (serving React pages), not the API — the API is where
 * the 402 Lambda lives and where the facilitator round-trip fires.
 *
 * Same dual-host split we enforce on the backend: humans hit webapp,
 * the payment POST hits the API.
 */
import { paymentLinksAxios } from "../../api/paymentLinksApi";

/**
 * Resolve the correct API URL to POST the X-PAYMENT header to.
 *
 * The /info response's `urls.full` is the UUID form on the API host
 * — already the right URL for this request, no rewriting needed.
 * Prefer it unconditionally: it's always present (UUID is the
 * source-of-record) and its host is always the API. Short + slug
 * URLs live on the webapp host and would require a rewrite; using
 * `urls.full` avoids that whole branch.
 */
function resolveSettlementUrl(link) {
  if (link?.urls?.full) return link.urls.full;
  // Defensive fallback — /info always returns `urls.full` for new
  // rows, but a misshaped response shouldn't crash the page.
  const base =
    process.env.REACT_APP_API_BASE_URL || "https://dev-api.horuswallet.com";
  return `${base}/payment-link/${link.linkId}`;
}

/**
 * POST the signed X-PAYMENT header to the 402 Lambda. Returns the
 * parsed JSON body on 200 (the receipt); throws on any non-2xx with
 * a message the UI can render.
 *
 * The 402 Lambda may return:
 *   - 200: success, body = receipt
 *   - 402: verification failed / bad signature — payer can re-sign
 *   - 410: link gone (expired / maxed / stale owner / disabled)
 *   - 502: facilitator unreachable — transient, payer retries
 */
export async function submitX402Payment(link, xPaymentHeader) {
  const url = resolveSettlementUrl(link);
  // Use `axios.get` with a custom config so we pass `X-PAYMENT`
  // without hitting `paymentLinksAxios`'s baseURL (url is already
  // absolute). Setting `baseURL: ''` effectively disables it for
  // this call; `headers` shape matches what the Lambda reads.
  const res = await paymentLinksAxios.get(url, {
    baseURL: "",
    // Header name case matches the backend's CORS
    // `Access-Control-Allow-Headers: Content-Type, X-Payment`
    // preflight response exactly. Most browsers treat HTTP header
    // names case-insensitively per spec, but API Gateway + a few
    // proxies will still drop a response when the preflight name
    // ("X-Payment") drifts from the actual request name
    // ("X-PAYMENT"), which surfaces as an opaque "Network Error"
    // in axios.
    headers: { "X-Payment": xPaymentHeader },
    // Let the caller handle status-based branching instead of
    // axios throwing on 4xx; we always receive a JSON body with a
    // helpful error shape and want to surface it.
    validateStatus: () => true,
  });

  if (res.status === 200) {
    return res.data;
  }

  const body = res.data || {};
  const msg =
    body.error ||
    body.message ||
    `Payment failed (HTTP ${res.status}).`;
  const err = new Error(msg);
  err.statusCode = res.status;
  err.reason = body.reason || null;
  err.body = body;
  throw err;
}
